import { parsePhoneNumberWithError } from "libphonenumber-js";

/**
 * Formats a phone number to international format without spaces.
 * Returns undefined if the number is invalid.
 *
 * @param number - Phone number string to format
 * @returns Formatted phone number or undefined
 *
 * @example
 * formatPhoneNumber("(555) 123-4567") // "+15551234567"
 */
export function formatPhoneNumber(number: string): string | undefined {
  try {
    const parsed = parsePhoneNumberWithError(number, "US");
    if (!parsed.isValid()) {
      return undefined;
    }
    return parsed.formatInternational().replace(/ /g, "");
  } catch {
    return undefined;
  }
}

/**
 * Normalizes date strings from MM/DD/YYYY to YYYY-MM-DD format.
 * This allows datepicker components that output MM/DD/YYYY to be validated
 * using strict ISO date validation.
 *
 * @param value - Date string in either MM/DD/YYYY or YYYY-MM-DD format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * normalizeDateString("04/31/2024") // "2024-04-31" (will be rejected by validation)
 * normalizeDateString("2024-01-15") // "2024-01-15" (unchanged)
 */
export function normalizeDateString(value: string): string {
  if (!value) {
    return value;
  }

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const usFormat = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  if (usFormat.test(value)) {
    return value.replace(usFormat, "$3-$1-$2");
  }

  return value;
}
